import { useState, useCallback } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';

const functions = getFunctions(undefined, 'europe-west2');
const refreshHiveWeatherFn = httpsCallable(functions, 'refreshHiveWeather');

interface WeatherLocation {
  lat: number;
  lon: number;
  timezone: string;
}

interface CurrentWeather {
  time: string;
  temperature_2m: number;
  weather_code: number;
  relative_humidity_2m: number;
  wind_speed_10m: number;
  wind_direction_10m: number;
}

interface HourlyWeather {
  time: string;
  temperature_2m: number;
  weather_code: number;
  precipitation_probability: number;
  wind_speed_10m: number;
}

interface DailyWeather {
  date: string;
  temperature_2m_max: number;
  temperature_2m_min: number;
  weather_code: number;
  precipitation_probability_max: number;
  wind_speed_10m_max: number;
}

interface HiveWeather {
  updatedAt: string;
  location: WeatherLocation;
  current: CurrentWeather;
  hourly24h: HourlyWeather[];
  daily7d: DailyWeather[];
  nextRefreshAfter: string;
}

interface WeatherSnapshot {
  snapshotAt: string;
  hiveWeather: {
    updatedAt: string;
    location: WeatherLocation;
    current: CurrentWeather;
    hourlyRecent: HourlyWeather[];
    dailyToday: DailyWeather | null;
  };
}

// Weather code to condition mapping
const weatherCodeToCondition = (code: number): string => {
  if (code <= 1) return 'clear';
  if (code <= 3) return 'partly-cloudy';
  if (code <= 48) return 'cloudy';
  if (code <= 67) return 'rain';
  if (code <= 77) return 'snow';
  if (code <= 82) return 'rain';
  if (code <= 86) return 'snow';
  if (code <= 99) return 'thunderstorm';
  return 'unknown';
};

// Weather code to description mapping
const weatherCodeToDescription = (code: number): string => {
  const descriptions: Record<number, string> = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Fog',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    56: 'Light freezing drizzle',
    57: 'Dense freezing drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    66: 'Light freezing rain',
    67: 'Heavy freezing rain',
    71: 'Slight snow fall',
    73: 'Moderate snow fall',
    75: 'Heavy snow fall',
    77: 'Snow grains',
    80: 'Slight rain showers',
    81: 'Moderate rain showers',
    82: 'Violent rain showers',
    85: 'Slight snow showers',
    86: 'Heavy snow showers',
    95: 'Thunderstorm',
    96: 'Thunderstorm with slight hail',
    99: 'Thunderstorm with heavy hail',
  };
  return descriptions[code] || `Unknown (${code})`;
};

export const useHiveWeather = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshWeather = useCallback(async (hiveId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await refreshHiveWeatherFn({ hiveId });
      const data = result.data as { 
        refreshed?: boolean; 
        skipped?: boolean; 
        weather?: HiveWeather; 
        nextRefreshAfter?: string; 
      };
      
      return data;
    } catch (err: any) {
      console.error('Failed to refresh weather:', err);
      setError(err.message || 'Failed to refresh weather');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const formatTemperature = useCallback((temp: number) => {
    return `${Math.round(temp)}°C`;
  }, []);

  const formatWind = useCallback((speed: number, direction?: number) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 
                       'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const dirStr = direction !== undefined 
      ? directions[Math.round(direction / 22.5) % 16]
      : '';
    
    return `${Math.round(speed * 3.6)} km/h${dirStr ? ` ${dirStr}` : ''}`;
  }, []);

  const isWeatherDue = useCallback((weather?: HiveWeather) => {
    if (!weather?.nextRefreshAfter) return true;
    return new Date(weather.nextRefreshAfter) <= new Date();
  }, []);

  const getWeatherAge = useCallback((weather?: HiveWeather) => {
    if (!weather?.updatedAt) return null;
    
    const updated = new Date(weather.updatedAt);
    const now = new Date();
    const diffMs = now.getTime() - updated.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Recently updated';
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  }, []);

  return {
    loading,
    error,
    refreshWeather,
    formatTemperature,
    formatWind,
    isWeatherDue,
    getWeatherAge,
    weatherCodeToCondition,
    weatherCodeToDescription,
  };
};

export type {
  HiveWeather,
  WeatherSnapshot,
  CurrentWeather,
  HourlyWeather,
  DailyWeather,
  WeatherLocation,
};
